/* Green Hills Coffee USA — UX layer
   Every block is a progressive enhancement: if a node or an API is missing the
   feature simply does not run, and nothing the customer needs is ever hidden.
   Markup lives in snippets/gh-ux.liquid, styles in assets/gh-ux.css. */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function onRaf(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; fn(); });
    };
  }

  /* ---- Dark mode toggle ----
     The initial value is set by the inline script in theme.liquid so the page
     never flashes the wrong theme; here we only handle switching. */
  function initTheme() {
    var buttons = document.querySelectorAll('[data-gh-theme-toggle]');
    if (!buttons.length) return;

    function label(theme) {
      return theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
    }
    function apply(theme) {
      root.setAttribute('data-gh-theme', theme);
      try { localStorage.setItem('gh-theme', theme); } catch (e) {}
      buttons.forEach(function (b) {
        b.setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
        b.setAttribute('aria-label', label(theme));
        b.setAttribute('title', label(theme));
      });
    }
    apply(root.getAttribute('data-gh-theme') === 'dark' ? 'dark' : 'light');
    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        apply(root.getAttribute('data-gh-theme') === 'dark' ? 'light' : 'dark');
      });
    });
  }

  /* ---- Scroll progress bar ---- */
  function initProgress() {
    var fill = document.querySelector('[data-gh-progress-fill]');
    if (!fill) return;
    var update = onRaf(function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? Math.min(1, Math.max(0, window.scrollY / h)) : 0;
      fill.style.transform = 'scaleX(' + p + ')';
    });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---- Back to top ---- */
  function initBackToTop() {
    var btn = document.querySelector('[data-gh-totop]');
    if (!btn) return;
    var update = onRaf(function () {
      btn.classList.toggle('is-on', window.scrollY > 700);
    });
    window.addEventListener('scroll', update, { passive: true });
    update();
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });
  }

  /* ---- Sticky CTA ----
     Phones: always on, so a buy path is one tap away on every page.
     Desktop: product pages only, sliding in once the visitor scrolls. */
  function initStickyCta() {
    var bar = document.querySelector('[data-gh-sticky]');
    if (!bar) return;

    var isProduct = bar.hasAttribute('data-gh-sticky-product');
    var mobile = window.matchMedia('(max-width: 749px)');
    var form = document.querySelector('.pdp__form');
    var atc = document.querySelector('[data-gh-atc]');
    var atcLabel = document.querySelector('[data-gh-atc-label]');
    var priceNow = document.querySelector('[data-gh-price-now]');
    var stickyBtn = bar.querySelector('[data-gh-sticky-atc]');
    var stickyLabel = bar.querySelector('[data-gh-sticky-label]');
    var stickyPrice = bar.querySelector('[data-gh-sticky-price]');
    var visible = null;

    function setHeight() {
      root.style.setProperty('--gh-sticky-h', bar.offsetHeight + 'px');
    }

    function show(next) {
      if (next === visible) return;
      visible = next;
      if (next) {
        bar.hidden = false;
        setHeight();
        document.body.classList.add('gh-sticky-on');
        requestAnimationFrame(function () { bar.classList.add('is-on'); });
      } else {
        bar.classList.remove('is-on');
        document.body.classList.remove('gh-sticky-on');
        root.style.setProperty('--gh-sticky-h', '0px');
        window.setTimeout(function () { if (!visible) bar.hidden = true; }, 320);
      }
    }

    // Phones: always visible, on every page that renders the bar.
    // Desktop: product pages only, sliding in as soon as the visitor scrolls.
    // (The buy column is position:sticky on desktop, so waiting for the main
    // button to leave the viewport meant the bar almost never showed.)
    var SCROLL_TRIGGER = 120;
    var update = onRaf(function () {
      if (mobile.matches) { show(true); return; }
      show(isProduct && window.scrollY > SCROLL_TRIGGER);
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', function () { update(); if (visible) setHeight(); });
    if (mobile.addEventListener) mobile.addEventListener('change', update);
    update();

    if (!isProduct || !form) return;

    // Mirror the live label and price the product script keeps up to date.
    function mirror() {
      if (stickyLabel && atcLabel) stickyLabel.textContent = atcLabel.textContent;
      if (stickyPrice && priceNow) stickyPrice.textContent = priceNow.textContent;
      if (stickyBtn && atc) stickyBtn.disabled = atc.disabled;
    }
    if (window.MutationObserver) {
      var mo = new MutationObserver(mirror);
      if (atcLabel) mo.observe(atcLabel, { childList: true, characterData: true, subtree: true });
      if (priceNow) mo.observe(priceNow, { childList: true, characterData: true, subtree: true });
      if (atc) mo.observe(atc, { attributes: true, attributeFilter: ['disabled'] });
    }
    mirror();

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled) return;
        // Route through the real form so the AJAX add-to-cart and its
        // confirmation dialog stay the single source of truth.
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else if (atc) atc.click();
      });
    }
  }

  /* ---- Add to cart loading state ---- */
  function initButtonLoading() {
    var form = document.querySelector('.pdp__form');
    var atc = document.querySelector('[data-gh-atc]');
    if (!form || !atc) return;
    var sticky = document.querySelector('[data-gh-sticky-atc]');

    function setLoading(on) {
      atc.classList.toggle('is-loading', on);
      if (sticky) sticky.classList.toggle('is-loading', on);
    }
    form.addEventListener('submit', function () {
      if (!atc.disabled) setLoading(true);
    });
    // The product script re-enables the button when the request settles.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!atc.disabled) setLoading(false);
      }).observe(atc, { attributes: true, attributeFilter: ['disabled'] });
    }
    window.setTimeout(function () { setLoading(false); }, 8000);
  }

  /* ---- Page loading bar on navigation ---- */
  function initLoadBar() {
    var bar = document.querySelector('[data-gh-loadbar]');
    if (!bar) return;
    var fill = bar.querySelector('[data-gh-loadbar-fill]');
    var timer = null;

    function start() {
      if (timer) return;
      var pct = 8;
      bar.classList.add('is-on');
      if (fill) fill.style.width = pct + '%';
      timer = window.setInterval(function () {
        pct = pct + (90 - pct) * 0.12;
        if (fill) fill.style.width = pct + '%';
      }, 220);
    }
    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
      bar.classList.remove('is-on');
      if (fill) fill.style.width = '0';
    }

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      if (a.origin && a.origin !== window.location.origin) return;
      if (a.pathname === window.location.pathname && a.search === window.location.search) return;
      start();
    });
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.classList && f.classList.contains('pdp__form')) return; // handled by AJAX
      start();
    });
    // Restore a clean bar when the browser serves the page from bfcache.
    window.addEventListener('pageshow', stop);
  }

  /* ---- Lazy image fade-in ---- */
  function initImageFade() {
    if (reduced) return;
    var imgs = [].slice.call(document.querySelectorAll('img[loading="lazy"]'));
    if (!imgs.length) return;
    var pending = [];
    imgs.forEach(function (img) {
      if (img.complete) return;
      img.classList.add('gh-img-fade');
      pending.push(img);
      img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
      img.addEventListener('error', function () { img.classList.add('is-loaded'); }, { once: true });
    });
    // Failsafe: never leave an image invisible because an event was missed.
    window.setTimeout(function () {
      pending.forEach(function (img) { img.classList.add('is-loaded'); });
    }, 3000);
  }

  /* ---- Cursor companion ---- */
  function initCursor() {
    if (reduced || !root.hasAttribute('data-gh-cursor')) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var dot = document.createElement('div');
    dot.className = 'gh-cursor';
    dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);

    var x = 0, y = 0, cx = 0, cy = 0, started = false, raf = null;
    var hot = 'a, button, summary, input, select, textarea, label, .chip, .swatch, [role="button"]';

    function loop() {
      cx += (x - cx) * 0.18;
      cy += (y - cy) * 0.18;
      dot.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
      raf = requestAnimationFrame(loop);
    }
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      x = e.clientX; y = e.clientY;
      if (!started) {
        started = true; cx = x; cy = y;
        dot.classList.add('is-on');
        loop();
      }
      var target = e.target.closest ? e.target.closest(hot) : null;
      dot.classList.toggle('is-hot', !!target);
    }, { passive: true });
    document.addEventListener('pointerdown', function () { dot.classList.add('is-down'); });
    document.addEventListener('pointerup', function () { dot.classList.remove('is-down'); });
    document.addEventListener('mouseleave', function () { dot.classList.remove('is-on'); });
    document.addEventListener('mouseenter', function () { if (started) dot.classList.add('is-on'); });
  }

  /* ---- Cookie banner ----
     The answer is registered through Shopify's Consent Tracking API, so it
     reaches Shopify's own pixels and any app that reads consent. On top of
     that we keep our own record of the answer, because the API alone cannot
     tell an accept from a decline once the banner is dismissed:

       accept  -> stored for as long as the browser allows (localStorage plus a
                  400-day cookie, the longest life a browser grants a cookie
                  written from JS) and the banner is never shown again.
       decline -> not stored as settled, so the banner comes back on the next
                  page view, or after the grace period set in the theme editor.

     Shopify cookies are never read or written directly (see the Customer
     Privacy API docs); only our own gh-cc key is. */
  var CC_KEY = 'gh-cc';
  var CC_MAX_AGE = 34560000; // 400 days, in seconds

  function ccRead() {
    var raw = '';
    try { raw = window.localStorage.getItem(CC_KEY) || ''; } catch (e) {}
    if (!raw) {
      var hit = document.cookie.match(/(?:^|;\s*)gh-cc=([^;]*)/);
      raw = hit ? decodeURIComponent(hit[1]) : '';
    }
    if (!raw) return null;
    var parts = raw.split('|'); // "accept|1758528000000"
    return { answer: parts[0], at: parseInt(parts[1], 10) || 0 };
  }

  function ccWrite(answer) {
    var raw = answer + '|' + Date.now();
    try { window.localStorage.setItem(CC_KEY, raw); } catch (e) {}
    try {
      document.cookie = CC_KEY + '=' + encodeURIComponent(raw) +
        ';path=/;max-age=' + CC_MAX_AGE + ';samesite=lax' +
        (window.location.protocol === 'https:' ? ';secure' : '');
    } catch (e) {}
  }

  function initCookies() {
    var el = document.querySelector('[data-gh-cookies]');
    if (!el) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    var always = el.getAttribute('data-gh-cookies-mode') === 'always';
    var reask = parseFloat(el.getAttribute('data-gh-cookies-reask')) || 0; // hours, 0 = every page view

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (error) return;
      var api = window.Shopify.customerPrivacy;
      if (!api) return;

      var current = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
      var granted = !!current && (current.analytics === 'yes' || current.marketing === 'yes' || current.preferences === 'yes');
      var saved = ccRead();
      var declined = !!saved && saved.answer === 'decline';

      // Already accepted, on this visit or an earlier one: stay quiet.
      if (granted || (saved && saved.answer === 'accept')) return;
      // Declined with a grace period still running: stay quiet until it ends.
      if (declined && reask > 0 && Date.now() - saved.at < reask * 3600000) return;

      // shouldShowBanner() turns false as soon as any answer is on file, so a
      // visitor who declined is recognised by our own record instead.
      var required = always || declined;
      if (!required && typeof api.shouldShowBanner === 'function') required = api.shouldShowBanner();
      if (!required) return;

      // A short delay keeps the banner out of the way of the first paint.
      window.setTimeout(function () {
        el.hidden = false;
        requestAnimationFrame(function () { el.classList.add('is-on'); });
      }, declined ? 1200 : 600);

      function hide() {
        el.classList.remove('is-on');
        window.setTimeout(function () { el.hidden = true; }, 320);
      }
      function answer(value) {
        ccWrite(value ? 'accept' : 'decline');
        hide();
        try {
          api.setTrackingConsent({
            analytics: value,
            marketing: value,
            preferences: value,
            sale_of_data: value
          }, function () {});
        } catch (e) {}
      }
      el.querySelectorAll('[data-gh-cookies-accept]').forEach(function (b) {
        b.addEventListener('click', function () { answer(true); });
      });
      el.querySelectorAll('[data-gh-cookies-decline]').forEach(function (b) {
        b.addEventListener('click', function () { answer(false); });
      });
    });
  }

  ready(function () {
    initTheme();
    initProgress();
    initBackToTop();
    initStickyCta();
    initButtonLoading();
    initLoadBar();
    initImageFade();
    initCursor();
    initCookies();
  });
})();
